import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import NotesForm, { NoteFormData } from '../../components/common/NotesForm';
import ThankYouModal from '../../components/common/ThankYouModal';
import { createPublicNote } from '../../services/notesService';
import { PAGES } from '../../seo/documentHead';

/**
 * PublicNoteSubmissionPage - Page for unauthenticated users to submit notes
 * Accessible at /submit
 * Collects contributor information and note content
 */
const PublicNoteSubmissionPage: React.FC = () => {
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [submittedNoteId, setSubmittedNoteId] = useState<string | undefined>();

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Track QR code scans via displayId URL parameter
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const displayId = searchParams.get('displayId');

    if (displayId) {
      ReactGA.event({
        category: 'engagement',
        action: 'qr_code_scanned',
        label: displayId,
      });
    }
  }, [location.search]);

  // Handle form submission for public note creation
  const handleSubmit = async (formData: NoteFormData): Promise<string> => {
    try {
      setError(null);

      // Create public note - backend expects contributorInfo in the body
      const newNote = await createPublicNote(formData);

      // Return the note ID for the modal
      return newNote._id;
    } catch (err) {
      console.error('Error submitting public note:', err);
      setError('Failed to submit the note. Please try again later.');
      throw err;
    }
  };

  // Handle successful submission - show the modal
  const handleSubmitSuccess = (noteId: string) => {
    setSubmittedNoteId(noteId);
    setShowModal(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowModal(false);
  };

  return (
    <div className="container py-4">
      <div className="row mb-4">
        <div className="col">
          <h1 className="display-5">{PAGES['/submit'].h1}</h1>
          <p className="lead">{PAGES['/submit'].intro}</p>
        </div>
      </div>

      {/* Login Option Banner */}
      <div className="row mb-4">
        <div className="col">
          <div className="alert d-flex align-items-center mb-4" style={{
            backgroundColor: '#F4F6F7',
            border: '1px solid #E0E5E4',
            color: '#1C2826'
          }}>
            <i className="fas fa-info-circle me-3" style={{ fontSize: '1.5rem', color: '#7FA99B' }}></i>
            <div>
              <h5 className="mb-1" style={{color: '#1C2826'}}>Public Note Submission</h5>
              <p className="mb-0" style={{color: '#556862'}}>
                Your note will be reviewed before being displayed. You can submit anonymously or create an account to track your contributions.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row">
        <div className="col-12">
          {error && (
            <div className="alert alert-danger mb-4" role="alert">
              {error}
            </div>
          )}
          
          <div className="card">
            <div className="card-body">
              <NotesForm
                isEdit={false}
                onSubmit={handleSubmit}
                isAuthenticated={false}
                skipNavigation={true}
                onSubmitSuccess={handleSubmitSuccess}
              />
            </div>
          </div>
        </div>
      </div>

      <ThankYouModal
        isOpen={showModal}
        onClose={handleModalClose}
        isAuthenticated={false}
        noteId={submittedNoteId}
      />
    </div>
  );
};

export default PublicNoteSubmissionPage; 